import LoadingSpinner from '@common/src/components/LoadingSpinner';
import FlowProductsLayout from '@flows/src/components/flow-products/FlowProductsLayout';
import useAnvaFlowProductsBasketStore from '@flows/src/store/zustand/flows-products/useAnvaFlowProductsBasketStore';
import useAnvaFlowProductSelectionStore from '@flows/src/store/zustand/flows-products/useAnvaFlowProductSelectionStore';
import useYellowhiveFlowProductSelectionStore from '@flows/src/store/zustand/flows-products/useYellowhiveFlowProductSelectionStore';
import { ErrorBoundary } from '@sentry/react';
import { FC, lazy, Suspense, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { Redirect, Route, Switch } from 'react-router';
const FlowProductsOverview = lazy(
  () => import('@flows/src/pages/flow-products/FlowProductsOverview'),
);
const FlowProductsBasketOverview = lazy(
  () => import('@flows/src/pages/flow-products/FlowProductsBasketOverview'),
);
const FlowProductsBasket = lazy(
  () => import('@flows/src/pages/flow-products/FlowProductsBasket'),
);

export const FLOWS_PRODUCTS_PATH = '/flow-products';

const FlowsProductsRouter: FC = () => {
  const queryClient = useQueryClient();

  // Anva store resets
  const resetSelectionStore = useAnvaFlowProductSelectionStore(
    (state) => state.reset,
  );
  const resetBasketStore = useAnvaFlowProductsBasketStore(
    (state) => state.reset,
  );

  // YellowHive store resets
  const resetYellowhiveSelectionStore = useYellowhiveFlowProductSelectionStore(
    (state) => state.resetYellowhive,
  );
  useEffect(() => {
    // Reset all stores on mount
    resetSelectionStore();
    resetBasketStore();
    resetYellowhiveSelectionStore();

    return () => {
      // Reset all stores on unmount
      resetSelectionStore();
      resetBasketStore();
      resetYellowhiveSelectionStore();

      // Clear both initial job data and completed quotes cache when exiting flow
      queryClient.removeQueries(['flow-products', 'anva', 'quotes']);
      queryClient.removeQueries(['flow-products', 'anva', 'completed-quotes']);
      queryClient.removeQueries(['flow-products', 'yellowhive', 'quotes']);
      queryClient.removeQueries([
        'flow-products',
        'yellowhive',
        'completed-quotes',
      ]);
    };
  }, []);

  return (
    <ErrorBoundary>
      <FlowProductsLayout>
        <Switch>
          <Redirect
            from={`${FLOWS_PRODUCTS_PATH}/:flowId`}
            to={`${FLOWS_PRODUCTS_PATH}/:flowId/overview`}
            exact
          />
          <Route
            path={`${FLOWS_PRODUCTS_PATH}/:flowId/overview/:tagId?`}
            exact
            render={() => (
              <Suspense fallback={<LoadingSpinner />}>
                <FlowProductsOverview />
              </Suspense>
            )}
          />
          <Route
            path={`${FLOWS_PRODUCTS_PATH}/:flowId/basket`}
            render={() => (
              <Suspense fallback={<LoadingSpinner />}>
                <FlowProductsBasket />
              </Suspense>
            )}
          />
          <Route
            path={`${FLOWS_PRODUCTS_PATH}/:flowId/selection-overview`}
            render={() => (
              <Suspense fallback={<LoadingSpinner />}>
                <FlowProductsBasketOverview />
              </Suspense>
            )}
          />
        </Switch>
      </FlowProductsLayout>
    </ErrorBoundary>
  );
};

export default FlowsProductsRouter;
